import {
  Alert,
  type AlertProps,
  Alignment,
  Direction,
  Displaying,
  FontWeight,
  Spacing,
  Typography,
} from "@bonusx-global/bx-design-system";
import Box from "@mui/material/Box";
import emojiRegex from "emoji-regex";
import type { SurveyStepBodyExtra } from "../../../../legacy/pages/survey/entities/survey-step";
import { resolveSurveyIcon } from "../../utils/resolve-survey-icon";

const ALERT_EXTRA_COMPONENT = "alert";
type AlertSeverity = NonNullable<AlertProps["severity"]>;

const ALERT_SEVERITIES: readonly AlertSeverity[] = ["success", "info", "warning", "error", "urgency"];
const DEFAULT_ALERT_SEVERITY: AlertSeverity = "info";

export const SURVEY_RICH_TEXT_SX = { whiteSpace: "pre-line", "& b, & strong": { fontWeight: FontWeight.bold } };

interface AlertIconProps {
  icon?: JSX.Element;
  emoji?: string;
}

function toAlertSeverity(variant: string | undefined): AlertSeverity {
  const isKnownSeverity = ALERT_SEVERITIES.some(function isSameSeverity(severity) {
    return severity === variant;
  });
  return isKnownSeverity ? (variant as AlertSeverity) : DEFAULT_ALERT_SEVERITY;
}

function resolveAlertIconProps(icon: string | undefined): AlertIconProps {
  if (!icon) {
    return {};
  }
  const resolvedIcon = resolveSurveyIcon(icon);
  if (resolvedIcon) {
    return { icon: resolvedIcon };
  }
  const isLiteralEmoji = emojiRegex().test(icon);
  return isLiteralEmoji ? { emoji: icon } : {};
}

export function isAlertExtra(extra: SurveyStepBodyExtra): boolean {
  return extra.component === ALERT_EXTRA_COMPONENT;
}

interface SurveyExtraAlertsProps {
  extra: SurveyStepBodyExtra[] | undefined;
  fullWidth: boolean;
  maxWidth?: string;
}

function SurveyExtraAlerts({ extra, fullWidth, maxWidth }: Readonly<SurveyExtraAlertsProps>): JSX.Element | null {
  const alerts = extra?.filter(isAlertExtra) ?? [];
  if (alerts.length === 0) {
    return null;
  }

  return (
    <Box
      display={Displaying.flex}
      flexDirection={Direction.column}
      alignItems={fullWidth ? Alignment.stretch : Alignment.center}
      gap={Spacing["1000"]}
      width="100%"
      maxWidth={maxWidth}
    >
      {alerts.map(function renderAlert(alert) {
        return (
          <Alert
            key={`${alert.variant}-${alert.icon}-${alert.description}`}
            severity={toAlertSeverity(alert.variant)}
            {...resolveAlertIconProps(alert.icon)}
            sx={{ width: fullWidth ? "100%" : "auto", alignItems: Alignment.center }}
          >
            <Typography
              variant="body2"
              component="div"
              sx={SURVEY_RICH_TEXT_SX}
              dangerouslySetInnerHTML={{ __html: alert.description }}
            />
          </Alert>
        );
      })}
    </Box>
  );
}

export default SurveyExtraAlerts;
